import { AxiosResponse } from 'axios';
import { useAxios } from '@/features/common/helpers/axios';
import { GlobalResponseType, UrlQuery } from '@/features/common/types';
import i18n from '@/i18n';
import { parseError } from '@/features/common/helpers/errorParser';
import { CarrierCodeType } from '@/features/shipment/types';
import { AvailableAccountsType, CarrierAccountType } from '../types';

export const { axiosService, baseURL } = useAxios();

export type CarrierAccountResponseType = {
  carrierAccount?: CarrierAccountType
} & GlobalResponseType;

const filterExceptionMessage = (err: GlobalResponseType): GlobalResponseType => {
  if (!err.errorType) return err;

  const messageKey = `carrierAccounts.errorMessages.${err.errorType}`;
  if (!i18n.te(messageKey)) return err;

  return {
    ...err,
    errorMessage: i18n.t(messageKey) as string,
  };
};

export const getAllAvailableServiceAccounts = async (): Promise<{
  data?: AvailableAccountsType[]
} & GlobalResponseType> => {
  try {
    const response: AxiosResponse = await axiosService.get(
      '/my-accounts/available-accounts',
    );

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
      data: response.data,
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};

export const getCarrierAccounts = async ({
  offset = 0,
  limit = 20,
  orderBy = 'id',
  orderByDesc = true,
}: UrlQuery = {}): Promise<{
  data?: CarrierAccountType[]
} & GlobalResponseType> => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const queryString = `?Offset=${offset}&Limit=${limit}&OrderBy=${orderBy}&OrderByDescending=${orderByDesc}`;

    const response: AxiosResponse = await axiosService.get(
      `/my-accounts?timestamp=${new Date().getTime()}`,
      // TODO: use pagination when backend is ready
      // `/my-accounts${queryString}`,
    );

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
      data: response.data,
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};

export const getCarrierAccountById = async (
  accountId: number,
):Promise <CarrierAccountResponseType> => {
  try {
    const response: AxiosResponse = await axiosService.get(
      `/my-accounts/${accountId}`,
    );
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
      carrierAccount: response.data,
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};

export const addCarrierAccount = async (
  carrierCode: keyof typeof CarrierCodeType,
  data: unknown,
): Promise<CarrierAccountResponseType> => {
  try {
    const response: AxiosResponse = await axiosService.post(
      `/my-accounts/${carrierCode}`, data,
    );
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
      carrierAccount: response.data,
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};

export const editCarrierAccount = async (
  accountId: number,
  data: unknown,
): Promise<CarrierAccountResponseType> => {
  try {
    const response: AxiosResponse = await axiosService.put(
      `/my-accounts/${accountId}`, data,
    );
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
      carrierAccount: response.data,
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};

export const deleteCarrierAccount = async (
  accountId: number,
): Promise<{
  deleted?: boolean
} & GlobalResponseType> => {
  try {
    await axiosService.delete(
      `/my-accounts/${accountId}`,
    );
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
      deleted: true,
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};
